/** @jsxImportSource react */
import { useEffect, useState } from 'react'
import { Alert, Button, Link } from '@mui/material'
import MergeDialog from './MergeDialog'
import { Link as RouterLink } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { recordsAPI } from '../../api/crm'
import { useTenantKey } from '../../hooks/useCrmQueries'
import { allFields } from './fieldValues'

const DELAY = 600

/**
 * Warns (never blocks) when another record of the type has the same email or phone as what's
 * being typed. Checked a moment after typing stops.
 */
const DuplicateWarning = ({ entityType, form, values, recordId, record, canMerge = false }) => {
  const [merging, setMerging] = useState(null)
  const tenantId = useTenantKey()
  const keys = Object.fromEntries(allFields(form)
    .filter((f) => f.fieldType === 'EMAIL' || f.fieldType === 'PHONE')
    .map((f) => [f.fieldKey, typeof values[f.fieldKey] === 'string' ? values[f.fieldKey].trim() : ''])
    .filter(([, v]) => v))
  const signature = JSON.stringify(keys)
  const [settled, setSettled] = useState(signature)
  useEffect(() => {
    const timer = setTimeout(() => setSettled(signature), DELAY)
    return () => clearTimeout(timer)
  }, [signature])

  const { data: matches = [] } = useQuery({
    queryKey: [tenantId, 'records', 'duplicates', entityType, settled, recordId],
    queryFn: () => recordsAPI.duplicates(entityType, JSON.parse(settled), recordId),
    enabled: settled !== '{}',
    staleTime: 30_000,
  })
  if (settled === '{}' || matches.length === 0) return null

  return (
    <Alert severity="warning" sx={{ mb: 2 }}>
      {matches.length === 1 ? 'This may already exist: ' : `${matches.length} records may be the same: `}
      {matches.map((m, i) => (
        <span key={m.record.id}>
          {i > 0 && ', '}
          <Link component={RouterLink} to={`/records/${m.record.entityType}/${m.record.id}`} target="_blank" rel="noopener">{m.record.title}</Link>
          {' '}(same {m.matchedOn.join(' and ').toLowerCase()})
          {/* an existing record can absorb the duplicate */}
          {canMerge && record && (
            <Button size="small" color="inherit" sx={{ ml: 0.5, py: 0, minWidth: 0 }} onClick={() => setMerging(m.record.id)}>Merge here</Button>
          )}
        </span>
      ))}
      {merging && <MergeDialog record={record} form={form} duplicateId={merging} onClose={() => setMerging(null)} />}
    </Alert>
  )
}

export default DuplicateWarning
